/**
 * Safely filters an array using the given predicate function.
 * Returns an empty array if the input is null, undefined, or empty.
 *
 * @param arr - The array to filter.
 * @param predicate - Optional predicate function to determine which elements to include.
 * @returns A new filtered array, or an empty array if the input is invalid or empty.
 */
export const safeFilter = <T>(
  arr?: T[] | null,
  predicate?: (value: T, index: number, array: T[]) => boolean,
): T[] => {
  if (!hasItems(arr)) return [];
  return predicate ? arr.filter(predicate) : arr;
};

/**
 * Safely maps an array using the given mapper function.
 * Returns an empty array if the input is null, undefined, or empty.
 * If no mapper is provided, returns the original array as-is.
 */
export function safeMap<T>(arr?: T[] | null): T[];
export function safeMap<T, U>(
  arr: T[] | undefined | null,
  mapper: (value: T, index: number, array: T[]) => U,
): U[];
export function safeMap<T, U = T>(
  arr?: T[] | null,
  mapper?: (value: T, index: number, array: T[]) => U,
): Array<T | U> {
  if (!hasItems(arr)) return [];
  return mapper ? arr.map(mapper) : arr;
}

/**
 * Finds the object with the minimum value for a given property in an array.
 * @param array The array of objects to search through.
 * @param key The property key to compare.
 * @returns The object with the minimum value, or null if the array is empty.
 */
export function getMinBy<T extends Record<string, any>>(array: T[], key: keyof T): T | null {
  if (!hasItems(array)) {
    return null;
  }
  return array.reduce((prev, curr) => (prev[key] <= curr[key] ? prev : curr));
}

/**
 * Finds the object with the maximum value for a given property in an array.
 * @param array The array of objects to search through.
 * @param key The property key to compare.
 * @returns The object with the maximum value, or null if the array is empty.
 */
export function getMaxBy<T extends Record<string, any>>(array: T[], key: keyof T): T | null {
  if (!hasItems(array)) {
    return null;
  }
  return array.reduce((prev, curr) => (prev[key] >= curr[key] ? prev : curr));
}

/**
 * Determines if the provided value is a non-empty array.
 *
 * @template T - The type of elements in the array.
 * @param value - The value to check, which can be an array, null, or undefined.
 * @returns A type guard indicating whether the value is a non-empty array.
 */
export function hasItems<T>(value: T[] | null | undefined): value is T[] {
  return Array.isArray(value) && value.length > 0;
}
